import { useShop } from '../stores/shop.js';
import { STORES, STORE_LABELS, type Store } from '../lib/store.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { cn } from '../lib/cn.js';

/**
 * Le sélecteur de magasin de `/courses` — les `.segments` du kit.
 *
 * Des segments, et non une seconde barre d'onglets soulignés : le magasin est le
 * **contexte** (il change la liste *et* le catalogue), les onglets en dessous sont la vue
 * dans ce contexte. Deux traitements identiques empilés rendraient les deux illisibles.
 *
 * `role="group"` + `aria-pressed` plutôt que des rôles d'onglets ARIA : ce sont les deux
 * faces d'un réglage, et les vrais onglets sont juste en dessous.
 */
export function StoreSwitch({
  onChange,
}: {
  onChange?: (store: Store) => void;
}): React.ReactElement {
  const store = useShop((s) => s.store);
  const setStore = useShop((s) => s.setStore);
  const isMobile = useIsMobile();

  function select(next: Store): void {
    if (next === store) return;
    setStore(next);
    onChange?.(next);
  }

  return (
    <div
      role="group"
      aria-label="Magasin"
      className={cn('segments', isMobile && 'segments-etendus')}
    >
      {STORES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => select(s)}
          aria-pressed={s === store}
          className="segment"
        >
          {STORE_LABELS[s]}
        </button>
      ))}
    </div>
  );
}
